import { BattleSim, computeMods, effectiveStats } from './battle';
import { nextInt } from './rng';
import {
  canBuildAt,
  cellAt,
  closedCells,
  createRun,
  drawUnit,
  grantXp,
  openPerkOfferIfDue,
  inBounds,
  rerollPrice,
  sellValue,
  setCell,
  unitAt,
  unitPrice,
  units,
  wallRepairPrice,
  waveGold,
} from './state';
import type { ProfileSnapshot } from './profile';
import { unitTraits } from './traits';
import { SimError, type Action, type Balance, type Cell, type MapDef, type ModifierId, type RunState } from './types';
import { waveFor } from './waves';

function requirePhase(state: RunState, phase: RunState['phase']): void {
  if (state.phase !== phase) throw new SimError('wrong_phase', `expected ${phase}, got ${state.phase}`);
}

function requireCell(state: RunState, c: Cell): void {
  if (!Number.isInteger(c.x) || !Number.isInteger(c.y) || !inBounds(state, c)) {
    throw new SimError('bad_cell', `(${c.x},${c.y})`);
  }
}

function requirePocket(state: RunState, i: number): void {
  if (!Number.isInteger(i) || i < 0 || i >= state.pocket.length) throw new SimError('bad_pocket', String(i));
}

function spend(state: RunState, amount: number): void {
  if (state.gold < amount) throw new SimError('no_gold', `need ${amount}, have ${state.gold}`);
  state.gold -= amount;
}

/**
 * Start the battle for the current wave. Returns a stepable sim; call finishBattle when done.
 * The client uses this to animate; applyAction('fight') uses it to run instantly.
 */
export function startBattle(state: RunState, map: MapDef, balance: Balance): BattleSim {
  requirePhase(state, 'placing');
  const wave = waveFor(map, state.wave);
  if (!wave) throw new SimError('no_wave', `wave ${state.wave} not in map ${map.id}`);
  state.actions.push({ t: 'fight' });
  state.phase = 'battle';
  return new BattleSim(state, balance, wave.spawns);
}

export function finishBattle(state: RunState, sim: BattleSim, balance: Balance): void {
  requirePhase(state, 'battle');
  if (!sim.done) throw new SimError('battle_not_done');
  state.wallHp = sim.wallHp;
  state.kills += sim.kills;
  state.gold += sim.gold;
  state.lostStructures += sim.lostStructures;
  state.battleTicks += sim.tick;
  if (!sim.cleared) {
    state.phase = 'lost';
    return;
  }
  state.gold += waveGold(state, state.wave, balance);
  // Broken buildings are rebuilt between waves; a wave only costs you their help during the fight.
  for (const d of sim.destroyed) {
    if (state.grid[d.index]?.kind === 'empty') state.grid[d.index] = { kind: 'unit', unit: d.unit };
  }
  // Mines pay out after every cleared wave (rebuilt ones included).
  const mods = computeMods(state.perks);
  for (const { unit } of units(state)) {
    if (unit.arch === 'mine') state.gold += effectiveStats(unit, balance, mods, state.cardLevels[unit.arch], state.skills).income;
  }
  // Interest (mine trait): a cut of what you kept, so saving for a big clearing is not dead money.
  for (const { unit } of units(state)) {
    if (unit.arch === 'mine' && unitTraits(unit, balance).includes('interest')) state.gold += Math.min(25, Math.floor((state.gold * 5) / 100));
  }
  if (!state.endless && state.wave >= state.totalWaves) {
    state.phase = 'won';
    return;
  }
  state.wave++;
  state.rerollsThisWave = 0;
  // The front moves up: one more row opens for building, the spawn row never does.
  state.buildRows = Math.min(state.height - 1, state.buildRows + state.buildRowsPerWave);
  grantXp(state, balance.xpWave, balance);
  state.phase = 'placing';
  openPerkOfferIfDue(state);
}

export function applyAction(state: RunState, action: Action, map: MapDef, balance: Balance): void {
  let checkPerks = false;
  switch (action.t) {
    case 'buy': {
      requirePhase(state, 'placing');
      requireCell(state, action.to);
      const arch = state.shop[action.slot];
      if (arch === undefined) throw new SimError('bad_slot');
      if (!canBuildAt(state, action.to)) throw new SimError('no_build_zone');
      const target = cellAt(state, action.to);
      if (target.kind === 'unit') {
        // Dropping a bought building onto an equal level-1 one merges straight from the shop, as in the reference.
        if (target.unit.arch !== arch || target.unit.level !== 1) throw new SimError('cell_not_empty');
        if (balance.maxLevel < 2) throw new SimError('max_level');
        spend(state, unitPrice(state, balance, arch));
        setCell(state, action.to, { kind: 'unit', unit: { id: target.unit.id, arch, level: 2 } });
        state.purchases++;
        state.shop[action.slot] = drawUnit(state, balance);
        state.merges++;
        grantXp(state, balance.xpMergeBase * 2, balance);
        checkPerks = true;
        break;
      }
      if (target.kind !== 'empty') throw new SimError('cell_not_empty');
      spend(state, unitPrice(state, balance, arch));
      setCell(state, action.to, { kind: 'unit', unit: { id: state.nextUnitId++, arch, level: 1 } });
      state.purchases++;
      state.shop[action.slot] = drawUnit(state, balance);
      break;
    }
    case 'stash': {
      requirePhase(state, 'placing');
      const arch = state.shop[action.slot];
      if (arch === undefined) throw new SimError('bad_slot');
      requirePocket(state, action.pocket);
      if (state.pocket[action.pocket] !== null) throw new SimError('pocket_full');
      spend(state, unitPrice(state, balance, arch));
      state.pocket[action.pocket] = { id: state.nextUnitId++, arch, level: 1 };
      state.purchases++;
      state.shop[action.slot] = drawUnit(state, balance);
      break;
    }
    case 'place': {
      requirePhase(state, 'placing');
      requireCell(state, action.to);
      requirePocket(state, action.pocket);
      const u = state.pocket[action.pocket];
      if (!u) throw new SimError('pocket_empty');
      if (!canBuildAt(state, action.to)) throw new SimError('no_build_zone');
      const target = cellAt(state, action.to);
      if (target.kind === 'unit') {
        if (target.unit.arch !== u.arch || target.unit.level !== u.level) throw new SimError('cell_not_empty');
        if (u.level >= balance.maxLevel) throw new SimError('max_level');
        setCell(state, action.to, { kind: 'unit', unit: { id: target.unit.id, arch: u.arch, level: u.level + 1 } });
        state.pocket[action.pocket] = null;
        state.merges++;
        grantXp(state, balance.xpMergeBase * (u.level + 1), balance);
        checkPerks = true;
        break;
      }
      if (target.kind !== 'empty') throw new SimError('cell_not_empty');
      setCell(state, action.to, { kind: 'unit', unit: u });
      state.pocket[action.pocket] = null;
      break;
    }
    case 'pocket': {
      requirePhase(state, 'placing');
      requireCell(state, action.from);
      requirePocket(state, action.pocket);
      const src = unitAt(state, action.from);
      if (!src) throw new SimError('no_unit');
      const held = state.pocket[action.pocket];
      if (held === null || held === undefined) {
        state.pocket[action.pocket] = src;
        setCell(state, action.from, { kind: 'empty' });
        break;
      }
      if (held.arch === src.arch && held.level === src.level) {
        if (src.level >= balance.maxLevel) throw new SimError('max_level');
        state.pocket[action.pocket] = { id: held.id, arch: held.arch, level: held.level + 1 };
        setCell(state, action.from, { kind: 'empty' });
        state.merges++;
        grantXp(state, balance.xpMergeBase * (held.level + 1), balance);
        checkPerks = true;
        break;
      }
      // Different buildings: the pocketed one takes the cell, the lifted one takes the slot.
      state.pocket[action.pocket] = src;
      setCell(state, action.from, { kind: 'unit', unit: held });
      break;
    }
    case 'sell_pocket': {
      requirePhase(state, 'placing');
      requirePocket(state, action.pocket);
      const u = state.pocket[action.pocket];
      if (!u) throw new SimError('pocket_empty');
      state.gold += sellValue(state, u, balance);
      state.pocket[action.pocket] = null;
      break;
    }
    case 'pocket_move': {
      requirePhase(state, 'placing');
      requirePocket(state, action.from);
      requirePocket(state, action.to);
      if (action.from === action.to) throw new SimError('same_cell');
      const src = state.pocket[action.from];
      if (!src) throw new SimError('pocket_empty');
      const dst = state.pocket[action.to] ?? null;
      if (dst && dst.arch === src.arch && dst.level === src.level) {
        if (src.level >= balance.maxLevel) throw new SimError('max_level');
        state.pocket[action.to] = { id: dst.id, arch: dst.arch, level: dst.level + 1 };
        state.pocket[action.from] = null;
        state.merges++;
        grantXp(state, balance.xpMergeBase * (dst.level + 1), balance);
        checkPerks = true;
        break;
      }
      state.pocket[action.to] = src;
      state.pocket[action.from] = dst;
      break;
    }
    case 'move': {
      requirePhase(state, 'placing');
      requireCell(state, action.from);
      requireCell(state, action.to);
      if (action.from.x === action.to.x && action.from.y === action.to.y) throw new SimError('same_cell');
      const src = unitAt(state, action.from);
      if (!src) throw new SimError('no_unit');
      if (!canBuildAt(state, action.to)) throw new SimError('no_build_zone');
      const dst = cellAt(state, action.to);
      if (dst.kind === 'closed' || dst.kind === 'base') throw new SimError('cell_closed');
      if (dst.kind === 'empty') {
        setCell(state, action.to, { kind: 'unit', unit: src });
        setCell(state, action.from, { kind: 'empty' });
        break;
      }
      const other = dst.unit;
      if (other.arch === src.arch && other.level === src.level) {
        if (src.level >= balance.maxLevel) throw new SimError('max_level');
        setCell(state, action.to, { kind: 'unit', unit: { id: other.id, arch: other.arch, level: other.level + 1 } });
        setCell(state, action.from, { kind: 'empty' });
        state.merges++;
        grantXp(state, balance.xpMergeBase * (other.level + 1), balance);
        checkPerks = true;
        break;
      }
      // Different units: swap.
      setCell(state, action.to, { kind: 'unit', unit: src });
      setCell(state, action.from, { kind: 'unit', unit: other });
      break;
    }
    case 'open': {
      requirePhase(state, 'placing');
      requireCell(state, action.cell);
      const cs = cellAt(state, action.cell);
      if (cs.kind !== 'closed') throw new SimError('not_closed');
      if (cs.cost <= 0) throw new SimError('permanent');
      if (!canBuildAt(state, action.cell)) throw new SimError('no_build_zone');
      spend(state, cs.cost);
      setCell(state, action.cell, { kind: 'empty' });
      break;
    }
    case 'sell': {
      requirePhase(state, 'placing');
      requireCell(state, action.cell);
      const u = unitAt(state, action.cell);
      if (!u) throw new SimError('no_unit');
      state.gold += sellValue(state, u, balance);
      setCell(state, action.cell, { kind: 'empty' });
      break;
    }
    case 'repair': {
      requirePhase(state, 'placing');
      if (state.modifiers.includes('glass_bunker')) throw new SimError('no_repair');
      if (state.wallHp >= state.wallMax) throw new SimError('wall_full');
      spend(state, wallRepairPrice(state, balance));
      state.wallBuys++;
      state.wallHp = Math.min(state.wallMax, state.wallHp + balance.wallRepair.hp);
      break;
    }
    case 'reroll': {
      requirePhase(state, 'placing');
      spend(state, rerollPrice(state, balance));
      state.rerollsThisWave++;
      for (let i = 0; i < state.shop.length; i++) state.shop[i] = drawUnit(state, balance);
      break;
    }
    case 'fight': {
      const sim = startBattle(state, map, balance);
      // startBattle already logged the action.
      sim.runToEnd();
      finishBattle(state, sim, balance);
      return;
    }
    case 'perk': {
      requirePhase(state, 'perk');
      const perk = state.perkOffer[action.index];
      if (perk === undefined) throw new SimError('bad_perk');
      state.perks.push(perk);
      state.perkOffer = [];
      state.pendingPerks = Math.max(0, state.pendingPerks - 1);
      if (perk === 'wall_max') {
        state.wallMax += 30;
        state.wallHp = Math.min(state.wallMax, state.wallHp + 30);
      } else if (perk === 'open_free') {
        const closed = closedCells(state);
        if (closed.length > 0) {
          const c = closed[nextInt(state.rng, closed.length)]!;
          setCell(state, c, { kind: 'empty' });
        }
      }
      state.phase = 'placing';
      checkPerks = true; // Several drafts may be queued after a big merge chain.
      break;
    }
    default: {
      const never: never = action;
      throw new SimError('unknown_action', String(never));
    }
  }
  state.actions.push(action);
  if (checkPerks) openPerkOfferIfDue(state);
}

/** Rebuild a run from scratch. This is what the server does to verify a submitted score. */
export function replayRun(
  map: MapDef,
  seed: number,
  actions: readonly Action[],
  balance: Balance,
  snapshot?: ProfileSnapshot,
  modifiers: readonly ModifierId[] = [],
): RunState {
  const state = createRun(map, seed, balance, snapshot, modifiers);
  for (const a of actions) applyAction(state, a, map, balance);
  return state;
}

export function scoreOf(state: RunState): number {
  const cleared = state.phase === 'won' ? state.totalWaves : state.wave - 1;
  return cleared * 1000 + state.wallHp;
}
